import { useState } from "react";
import { Link } from "react-router-dom";
import { Store, Globe, Code, Bell, Calendar, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";

export type PlatformType = "shopify" | "standalone" | "api_only";

interface PlatformOption {
  id: PlatformType;
  name: string;
  description: string;
  icon: React.ReactNode;
  badge?: string;
  comingSoon?: boolean;
}

// Shopify bag logo SVG component
const ShopifyLogo = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 109.5 124.5" fill="currentColor">
    <path d="M74.7,14.8c-0.1-0.1-1.8-0.4-1.8-0.4s-1.2-1.2-1.3-1.3c0,0,0,0-0.1,0c0,0-0.1,0-0.1,0l-3,0.9 c-0.4-1.1-0.9-2.4-1.6-3.8c-2.4-4.6-5.8-7.1-10-7.1c0,0,0,0,0,0c-0.3,0-0.6,0-0.9,0.1c-0.1-0.2-0.3-0.3-0.4-0.5 c-1.8-1.9-4.2-2.9-6.9-2.8c-5.4,0.2-10.7,4-15,10.8c-3,4.8-5.3,10.8-6,15.5c-6.1,1.9-10.4,3.2-10.5,3.3c-3.1,1-3.2,1.1-3.6,4 C13.2,35.3,0,140.6,0,140.6l75.4,13l32.4-8.1C107.8,145.4,74.8,14.9,74.7,14.8z M57.2,20l-4.3,1.3c0-0.2,0-0.5,0-0.7 c0-3.4-0.5-6.1-1.2-8.2C54.9,13,56.5,16.2,57.2,20z M47.5,13c0.8,2,1.3,4.9,1.3,8.8c0,0.3,0,0.6,0,0.8l-8.8,2.7 c1.7-6.5,4.9-9.6,7.5-12.3C47.5,13,47.5,13,47.5,13z M43.5,4.2c0.6,0,1.2,0.2,1.7,0.5c-3.6,1.7-7.4,5.9-9,14.4l-6.9,2.1 C31.6,13.3,37.3,4.4,43.5,4.2z"/>
  </svg>
);

const platformOptions: PlatformOption[] = [
  {
    id: "shopify",
    name: "Shopify Store",
    description: "Connect your Shopify store with one-click OAuth integration",
    icon: <ShopifyLogo className="h-6 w-6" />,
    badge: "Popular",
  },
  {
    id: "standalone",
    name: "Standalone / Other Platform",
    description: "Use with WooCommerce, custom stores, or any ecommerce platform",
    icon: <Globe className="h-6 w-6" />,
    comingSoon: true,
  },
  {
    id: "api_only",
    name: "API Integration",
    description: "Full API access for custom implementations and developers",
    icon: <Code className="h-6 w-6" />,
    comingSoon: true,
  },
];

interface PlatformSelectorProps {
  selectedPlatform: PlatformType | null;
  onSelect: (platform: PlatformType) => void;
  disabled?: boolean;
}

// NOTE: Using function declaration instead of const arrow to prevent TDZ errors in production builds
export function PlatformSelector({ 
  selectedPlatform, 
  onSelect, 
  disabled = false 
}: PlatformSelectorProps) {
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [notifyPlatform, setNotifyPlatform] = useState<string>("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    storeName: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleNotifyClick = (e: React.MouseEvent, platformName: string) => {
    e.preventDefault();
    e.stopPropagation();
    setNotifyPlatform(platformName);
    setNotifyDialogOpen(true);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleNotifySubmit = async () => {
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    if (!formData.name.trim()) {
      toast.error("Please enter your name");
      return;
    }

    setSubmitting(true);
    try {
      // Save to waitlist
      const { error: waitlistError } = await supabase
        .from('waitlist_signups')
        .insert({
          email: formData.email,
          source: `platform_interest_${notifyPlatform.toLowerCase().replace(/\s+/g, '_')}`,
        });

      if (waitlistError && waitlistError.code !== '23505') {
        throw waitlistError;
      }

      // Send notification email
      await supabase.functions.invoke('send-contact-email', {
        body: {
          name: formData.name,
          email: formData.email,
          subject: `Platform Interest: ${notifyPlatform}`,
          message: `
Name: ${formData.name}
Email: ${formData.email}
Store Name: ${formData.storeName || 'Not provided'}
Platform Interest: ${notifyPlatform}

Message:
${formData.message || 'No additional message'}
          `.trim(),
        },
      });

      toast.success(`Thanks ${formData.name}! We'll be in touch soon about ${notifyPlatform}.`);
      setNotifyDialogOpen(false);
      setFormData({ name: "", email: "", storeName: "", message: "" });
    } catch (error) {
      console.error("Error saving interest:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="space-y-3">
        {platformOptions.map((option) => (
          <div
            key={option.id}
            className={cn(
              "w-full p-4 rounded-lg border-2 text-left transition-all relative",
              !option.comingSoon && "hover:border-primary/50 hover:bg-accent/50 cursor-pointer",
              selectedPlatform === option.id 
                ? "border-primary bg-primary/5" 
                : "border-border",
              option.comingSoon && "opacity-80"
            )}
            onClick={() => !option.comingSoon && !disabled && onSelect(option.id)}
          >
            <div className="flex items-start gap-4">
              <div className={cn(
                "p-2 rounded-lg",
                selectedPlatform === option.id 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-muted text-muted-foreground"
              )}>
                {option.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-medium">{option.name}</h3>
                  {option.badge && !option.comingSoon && (
                    <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                      {option.badge}
                    </span>
                  )}
                  {option.comingSoon && (
                    <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                      Coming Soon
                    </span>
                  )}
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  {option.description}
                </p>
                {option.comingSoon && (
                  <button
                    type="button"
                    onClick={(e) => handleNotifyClick(e, option.name)}
                    className="inline-flex items-center gap-1.5 mt-2 text-xs text-primary hover:text-primary/80 font-medium transition-colors cursor-pointer z-10 relative"
                  >
                    <Bell className="h-3 w-3" />
                    Notify me, I'm interested
                  </button>
                )}
              </div>
              {selectedPlatform === option.id && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="h-5 w-5 rounded-full bg-primary flex items-center justify-center">
                    <svg className="h-3 w-3 text-primary-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Interested in {notifyPlatform}?</DialogTitle>
            <DialogDescription>
              Tell us about your store and we'll reach out when it's ready.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="notify-name">Your Name *</Label>
                <Input
                  id="notify-name"
                  type="text"
                  placeholder="John Doe"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notify-email">Email *</Label>
                <Input
                  id="notify-email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  disabled={submitting}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notify-store">Store Name</Label>
              <Input
                id="notify-store"
                type="text"
                placeholder="Your Store Name"
                value={formData.storeName}
                onChange={(e) => handleInputChange("storeName", e.target.value)}
                disabled={submitting}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notify-message">Tell us more (optional)</Label>
              <Textarea
                id="notify-message"
                placeholder="What platform are you using? Any specific requirements?"
                value={formData.message}
                onChange={(e) => handleInputChange("message", e.target.value)}
                disabled={submitting}
                rows={3}
              />
            </div>
            
            <Button 
              onClick={handleNotifySubmit} 
              className="w-full"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Bell className="w-4 h-4 mr-2" />
                  Notify Me
                </>
              )}
            </Button>
            
            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-background px-2 text-xs text-muted-foreground">
                or
              </span>
            </div>
            
            <Button 
              variant="outline" 
              className="w-full border-primary/30 text-primary hover:bg-primary/10"
              asChild
            >
              <Link to="/demo">
                <Calendar className="w-4 h-4 mr-2" />
                Book a Demo Call
              </Link>
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PlatformSelector;
